import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  Archive,
  Plus,
  Pencil,
  Loader2,
  TrendingUp,
  ThumbsUp,
  ThumbsDown,
  Award,
  Timer,
} from 'lucide-react';

interface Shelf {
  id: string;
  code: string;
  name: string;
  min_skill_level: 'A' | 'B' | 'C';
  shelf_type: 'standard' | 'paint' | 'composite';
  description: string | null;
  sort_order: number;
  is_active: boolean;
}

interface LevelRequirement {
  id: string;
  from_level: 'B' | 'C';
  to_level: 'A' | 'B';
  min_good_cuts: number;
}

interface OperatorProgress {
  id: string;
  full_name: string;
  skill_level: 'A' | 'B' | 'C' | null;
  good_cuts: number;
  defect_cuts: number;
  total_cuts: number;
  overtime_cuts: number;
  avg_duration_seconds: number | null;
}

const SHELF_TYPE_LABEL: Record<string, string> = {
  standard: 'Estándar',
  paint: 'Pintura',
  composite: 'Compuestas',
};

const emptyShelfForm = {
  code: '',
  name: '',
  min_skill_level: 'C' as 'A' | 'B' | 'C',
  shelf_type: 'standard' as 'standard' | 'paint' | 'composite',
  description: '',
  sort_order: 0,
  is_active: true,
};

export default function WorkshopShelves() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [requirements, setRequirements] = useState<LevelRequirement[]>([]);
  const [operators, setOperators] = useState<OperatorProgress[]>([]);

  // Diálogo estantería
  const [shelfDialogOpen, setShelfDialogOpen] = useState(false);
  const [editingShelf, setEditingShelf] = useState<Shelf | null>(null);
  const [shelfForm, setShelfForm] = useState(emptyShelfForm);
  const [savingShelf, setSavingShelf] = useState(false);

  // Ascenso
  const [promoting, setPromoting] = useState<{ operator: OperatorProgress; toLevel: 'A' | 'B' } | null>(null);
  const [savingPromotion, setSavingPromotion] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data: shelvesData } = await api
        .from('workshop_shelves')
        .select('*')
        .order('sort_order');
      setShelves(shelvesData || []);

      const { data: reqData } = await api
        .from('skill_level_requirements')
        .select('*')
        .order('from_level', { ascending: false });
      setRequirements(reqData || []);

      // Perfiles con nivel + estadísticas de corte (consultas directas + mapa por id)
      const { data: profilesData } = await api
        .from('profiles')
        .select('id,full_name,skill_level,is_active')
        .eq('is_active', true)
        .order('full_name');

      const { data: statsData } = await api
        .from('v_operator_cutting_stats')
        .select('*');
      const statsMap = new Map((statsData || []).map((s: any) => [s.operator_id, s]));

      const ops: OperatorProgress[] = (profilesData || [])
        .filter((p: any) => p.skill_level || statsMap.has(p.id))
        .map((p: any) => {
          const s: any = statsMap.get(p.id) || {};
          return {
            id: p.id,
            full_name: p.full_name,
            skill_level: p.skill_level || null,
            good_cuts: s.good_cuts || 0,
            defect_cuts: s.defect_cuts || 0,
            total_cuts: s.total_cuts || 0,
            overtime_cuts: s.overtime_cuts || 0,
            avg_duration_seconds: s.avg_duration_seconds || null,
          };
        });
      setOperators(ops);
    } catch (error: any) {
      toast({ title: 'Error', description: 'No se pudieron cargar los datos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ============ Estanterías ============

  const openNewShelf = () => {
    setEditingShelf(null);
    setShelfForm({ ...emptyShelfForm, sort_order: shelves.length + 1 });
    setShelfDialogOpen(true);
  };

  const openEditShelf = (shelf: Shelf) => {
    setEditingShelf(shelf);
    setShelfForm({
      code: shelf.code,
      name: shelf.name,
      min_skill_level: shelf.min_skill_level,
      shelf_type: shelf.shelf_type,
      description: shelf.description || '',
      sort_order: shelf.sort_order || 0,
      is_active: shelf.is_active,
    });
    setShelfDialogOpen(true);
  };

  const handleSaveShelf = async () => {
    if (!shelfForm.code.trim() || !shelfForm.name.trim()) {
      toast({ title: 'Error', description: 'Código y nombre son obligatorios', variant: 'destructive' });
      return;
    }
    setSavingShelf(true);
    try {
      const payload = {
        code: shelfForm.code.trim(),
        name: shelfForm.name.trim(),
        min_skill_level: shelfForm.min_skill_level,
        shelf_type: shelfForm.shelf_type,
        description: shelfForm.description.trim() || null,
        sort_order: shelfForm.sort_order,
        is_active: shelfForm.is_active,
        updated_at: new Date().toISOString(),
      };
      if (editingShelf) {
        const { error } = await api.from('workshop_shelves').update(payload).eq('id', editingShelf.id);
        if (error) throw error;
      } else {
        const { error } = await api.from('workshop_shelves').insert([payload]);
        if (error) throw error;
      }
      toast({ title: editingShelf ? 'Estantería actualizada' : 'Estantería creada' });
      setShelfDialogOpen(false);
      await loadAll();
    } catch (error: any) {
      const msg = error?.code === '23505'
        ? 'Ya existe una estantería con ese código'
        : error.message || 'No se pudo guardar';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSavingShelf(false);
    }
  };

  // ============ Progresión ============

  const updateRequirement = async (req: LevelRequirement, value: number) => {
    try {
      const { error } = await api
        .from('skill_level_requirements')
        .update({ min_good_cuts: value })
        .eq('id', req.id);
      if (error) throw error;
      setRequirements((prev) =>
        prev.map((r) => (r.id === req.id ? { ...r, min_good_cuts: value } : r))
      );
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const nextLevelOf = (op: OperatorProgress): { toLevel: 'A' | 'B'; required: number } | null => {
    if (!op.skill_level || op.skill_level === 'A') return null;
    const req = requirements.find((r) => r.from_level === op.skill_level);
    if (!req) return null;
    return { toLevel: req.to_level, required: req.min_good_cuts };
  };

  const handlePromote = async () => {
    if (!promoting) return;
    setSavingPromotion(true);
    try {
      const { error } = await api
        .from('profiles')
        .update({ skill_level: promoting.toLevel, updated_at: new Date().toISOString() })
        .eq('id', promoting.operator.id);
      if (error) throw error;
      toast({
        title: 'Operario ascendido',
        description: `${promoting.operator.full_name} ahora es nivel ${promoting.toLevel}`,
      });
      setPromoting(null);
      await loadAll();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSavingPromotion(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent flex items-center gap-3">
            <Archive className="w-8 h-8 text-primary" />
            Estanterías y Progresión — Corte Taller
          </h1>
          <p className="text-muted-foreground mt-2">
            Gestión de estanterías del taller de corte y progresión de nivel de los operarios
          </p>
        </div>

        <Tabs defaultValue="shelves">
          <TabsList>
            <TabsTrigger value="shelves">Estanterías</TabsTrigger>
            <TabsTrigger value="progression">Progresión de Operarios</TabsTrigger>
          </TabsList>

          {/* ============ TAB ESTANTERÍAS ============ */}
          <TabsContent value="shelves" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={openNewShelf} className="bg-gradient-magma hover:shadow-magma-hover">
                <Plus className="w-4 h-4 mr-2" />
                Nueva estantería
              </Button>
            </div>

            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Nivel mínimo</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Orden</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shelves.map((s) => (
                        <TableRow key={s.id} className={!s.is_active ? 'opacity-50' : ''}>
                          <TableCell className="font-mono font-medium">{s.code}</TableCell>
                          <TableCell>{s.name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">Nivel {s.min_skill_level}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={s.shelf_type === 'paint' ? 'border-purple-500 text-purple-500' : ''}
                            >
                              {SHELF_TYPE_LABEL[s.shelf_type]}
                            </Badge>
                          </TableCell>
                          <TableCell>{s.sort_order}</TableCell>
                          <TableCell>
                            {s.is_active ? (
                              <Badge className="bg-green-500 text-white">Activa</Badge>
                            ) : (
                              <Badge variant="outline">Inactiva</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={() => openEditShelf(s)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {shelves.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                            No hay estanterías. Crea la primera.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ TAB PROGRESIÓN ============ */}
          <TabsContent value="progression" className="space-y-4">
            {/* Umbrales configurables */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Mínimo de piezas bien cortadas para subir de nivel
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-6">
                {requirements.map((req) => (
                  <div key={req.id} className="flex items-center gap-3">
                    <Badge variant="secondary">
                      {req.from_level} → {req.to_level}
                    </Badge>
                    <Input
                      type="number"
                      min={1}
                      className="w-24"
                      defaultValue={req.min_good_cuts}
                      onBlur={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (v > 0 && v !== req.min_good_cuts) updateRequirement(req, v);
                      }}
                    />
                    <span className="text-sm text-muted-foreground">piezas OK</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Tabla de operarios */}
            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Operario</TableHead>
                        <TableHead>Nivel actual</TableHead>
                        <TableHead>
                          <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> OK</span>
                        </TableHead>
                        <TableHead>
                          <span className="flex items-center gap-1"><ThumbsDown className="w-3 h-3" /> Defectos</span>
                        </TableHead>
                        <TableHead>
                          <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> Excesos</span>
                        </TableHead>
                        <TableHead>Progreso al siguiente nivel</TableHead>
                        <TableHead className="w-40"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {operators.map((op) => {
                        const next = nextLevelOf(op);
                        const pct = next ? Math.min(100, (op.good_cuts / next.required) * 100) : null;
                        const ready = next && op.good_cuts >= next.required;
                        return (
                          <TableRow key={op.id}>
                            <TableCell className="font-medium">{op.full_name}</TableCell>
                            <TableCell>
                              {op.skill_level ? (
                                <Badge variant="secondary">Nivel {op.skill_level}</Badge>
                              ) : (
                                <Badge variant="outline">Sin nivel</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-green-600 font-semibold">{op.good_cuts}</TableCell>
                            <TableCell className="text-red-500">{op.defect_cuts}</TableCell>
                            <TableCell className="text-amber-500">{op.overtime_cuts}</TableCell>
                            <TableCell>
                              {next ? (
                                <div className="space-y-1 min-w-[160px]">
                                  <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>{op.good_cuts} / {next.required}</span>
                                    <span>→ Nivel {next.toLevel}</span>
                                  </div>
                                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                                    <div
                                      className={`h-full ${ready ? 'bg-green-500' : 'bg-primary'}`}
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              ) : op.skill_level === 'A' ? (
                                <span className="text-xs text-muted-foreground">Nivel máximo</span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {next && ready && (
                                <Button
                                  size="sm"
                                  onClick={() => setPromoting({ operator: op, toLevel: next.toLevel })}
                                  className="bg-gradient-magma hover:shadow-magma-hover"
                                >
                                  <Award className="w-4 h-4 mr-1" />
                                  Ascender a {next.toLevel}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {operators.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                            No hay operarios con nivel asignado ni cortes registrados
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Diálogo estantería */}
      <Dialog open={shelfDialogOpen} onOpenChange={setShelfDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingShelf ? 'Editar estantería' : 'Nueva estantería'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Código *</Label>
                <Input
                  value={shelfForm.code}
                  onChange={(e) => setShelfForm({ ...shelfForm, code: e.target.value.toUpperCase() })}
                  placeholder="EST-A1"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={shelfForm.sort_order}
                  onChange={(e) => setShelfForm({ ...shelfForm, sort_order: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Nombre *</Label>
              <Input
                value={shelfForm.name}
                onChange={(e) => setShelfForm({ ...shelfForm, name: e.target.value })}
                placeholder="Estantería piezas nivel A"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nivel mínimo de operario</Label>
                <Select
                  value={shelfForm.min_skill_level}
                  onValueChange={(v) => setShelfForm({ ...shelfForm, min_skill_level: v as 'A' | 'B' | 'C' })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">A (solo operarios A)</SelectItem>
                    <SelectItem value="B">B (operarios A y B)</SelectItem>
                    <SelectItem value="C">C (todos)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select
                  value={shelfForm.shelf_type}
                  onValueChange={(v) => setShelfForm({ ...shelfForm, shelf_type: v as any })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Estándar</SelectItem>
                    <SelectItem value="paint">Pintura (exclusiva piezas a pintar)</SelectItem>
                    <SelectItem value="composite">Compuestas (pegado de piezas)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Descripción</Label>
              <Textarea
                value={shelfForm.description}
                onChange={(e) => setShelfForm({ ...shelfForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Activa</Label>
              <Switch
                checked={shelfForm.is_active}
                onCheckedChange={(v) => setShelfForm({ ...shelfForm, is_active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShelfDialogOpen(false)} disabled={savingShelf}>
              Cancelar
            </Button>
            <Button onClick={handleSaveShelf} disabled={savingShelf}>
              {savingShelf && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingShelf ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación de ascenso */}
      <AlertDialog open={!!promoting} onOpenChange={(o) => !o && setPromoting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar ascenso de nivel</AlertDialogTitle>
            <AlertDialogDescription>
              {promoting && (
                <>
                  {promoting.operator.full_name} ha alcanzado el mínimo de piezas bien cortadas
                  ({promoting.operator.good_cuts} OK). ¿Ascenderlo de nivel{' '}
                  {promoting.operator.skill_level} a nivel {promoting.toLevel}? Verá las estanterías
                  de su nuevo nivel a partir de ahora.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingPromotion}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handlePromote} disabled={savingPromotion}>
              {savingPromotion && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar ascenso
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
